import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Star, ShieldCheck, Zap, ArrowRight, Brain, BarChart3, Target, Smartphone, Send, PlayCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Ticker from '../components/Ticker';
import SignalTerminal from '../components/SignalTerminal';
import { CONFIG, FEATURES, STRATEGIES, STATS, TESTIMONIALS } from '../mock/mock';

const ICONS = { Brain, Zap, BarChart3, Target, ShieldCheck, Smartphone };

export default function Home() {
  return (
    <div className="alpha-bg min-h-screen">
      <Header />
      <Ticker />

      <Hero />
      <Steps />
      <Tutorial />
      <Features />
      <StrategyEngine />
      <Stats />
      <Testimonials />
      <FinalCTA />
      <TelegramBlock />

      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 grid-overlay opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-2 gap-12 items-center relative">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/5 px-3 py-1 text-[11px] tracking-[0.3em] text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399]" /> LIVE AI ENGINE · ONLINE
          </div>
          <h1 className="font-display mt-5 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
            Unlock The Alpha<br />
            <span className="text-cyan-gradient">AI Trading Terminal</span>
          </h1>
          <p className="mt-6 max-w-xl text-slate-300/90 text-base sm:text-lg leading-relaxed">
            Access institutional-grade AI trading signals after verifying your <span className="text-cyan-300 font-medium">{CONFIG.brokerName}</span> account. Built with 33 professional trading strategies and synchronized with live market timing.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href={CONFIG.referralLink} target="_blank" rel="noreferrer" className="btn-cyan rounded-xl px-5 py-3 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-2">
              Create {CONFIG.brokerName} Account <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/verify" className="btn-ghost rounded-xl px-5 py-3 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-2">
              <ShieldCheck className="h-4 w-4 text-cyan-300" /> Verify Trader ID
            </Link>
            <Link to="/login" className="btn-ghost rounded-xl px-5 py-3 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-2">
              Member Login
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {[
              { icon: CheckCircle2, text: '132,938+ Traders', c: 'text-emerald-300' },
              { icon: Star, text: '5.0 / 5 Rating', c: 'text-amber-300' },
              { icon: ShieldCheck, text: 'Secure Verification', c: 'text-cyan-300' },
              { icon: Zap, text: 'Instant Access', c: 'text-yellow-300' },
            ].map((chip, i) => (
              <div key={i} className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 text-xs text-slate-200">
                <chip.icon className={`h-3.5 w-3.5 ${chip.c}`} /> {chip.text}
              </div>
            ))}
          </div>
        </div>

        <div className="relative lg:pl-6">
          <div className="absolute -inset-6 bg-cyan-500/10 blur-3xl rounded-full -z-10" />
          <SignalTerminal autoRotate interactive={false} fixedPair="EUR/USD OTC" />
        </div>
      </div>
    </section>
  );
}

function Steps() {
  const steps = [
    { n: '01', title: `Create ${CONFIG.brokerName} Account`, desc: 'Open your account using our affiliate link.' },
    { n: '02', title: 'Deposit $10+', desc: 'Fund your account with the minimum required.' },
    { n: '03', title: 'Verify Trader ID', desc: 'Approval usually takes under 2 minutes.' },
  ];
  return (
    <section className="relative py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionLabel eyebrow="3 Simple Steps" title="How It Works" />
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <div key={i} className="glass rounded-2xl p-6 feature-card">
              <div className="text-[11px] tracking-[0.3em] text-cyan-300/70">STEP {s.n}</div>
              <div className="mt-2 font-display text-xl font-semibold text-white">{s.title}</div>
              <p className="mt-2 text-sm text-slate-400">{s.desc}</p>
              {i < 2 && <ArrowRight className="mt-6 h-5 w-5 text-cyan-300/70" />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Tutorial() {
  const [play, setPlay] = useState(false);
  return (
    <section className="relative py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionLabel eyebrow="Full Walkthrough" title="Watch Full Tutorial" />
        <div className="mt-8 grid gap-8 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <div className="aspect-video glass-strong rounded-2xl overflow-hidden relative flex items-center justify-center">
              {!play ? (
                <button onClick={() => setPlay(true)} className="group flex flex-col items-center gap-3">
                  <div className="h-20 w-20 rounded-full btn-cyan flex items-center justify-center group-hover:scale-105 transition-transform">
                    <PlayCircle className="h-10 w-10" />
                  </div>
                  <span className="text-xs tracking-[0.3em] text-cyan-300">CLICK TO PLAY</span>
                </button>
              ) : (
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1"
                  title="Tutorial"
                  frameBorder="0"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              )}
            </div>
          </div>
          <div className="lg:col-span-2">
            <h3 className="font-display text-2xl font-semibold text-white">Watch this complete tutorial before getting started.</h3>
            <p className="mt-2 text-sm text-slate-400">This video explains:</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-300">
              {[
                `Creating your ${CONFIG.brokerName} account`,
                'Making the required deposit',
                'Finding your Trader ID',
                'Submitting your Trader ID',
                'Getting verified',
                'Logging in',
                'Using The Alpha Trader AI Dashboard',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5" /> {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="relative py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionLabel eyebrow="Premium Advantages" title="Why Choose The Alpha Trader AI" />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => {
            const Icon = ICONS[f.icon];
            return (
              <div key={i} className="glass rounded-2xl p-6 feature-card">
                <div className="h-11 w-11 rounded-lg glass flex items-center justify-center glow-cyan">
                  <Icon className="h-5 w-5 text-cyan-300" />
                </div>
                <div className="mt-4 font-display text-lg font-semibold text-white">{f.title}</div>
                <p className="mt-2 text-sm text-slate-400">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function StrategyEngine() {
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActiveIdx((i) => (i + 1) % STRATEGIES.length), 900);
    return () => clearInterval(t);
  }, []);
  return (
    <section className="relative py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionLabel eyebrow="AI Strategy Engine" title="Running 33 Professional Trading Strategies" />
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          <div className="glass rounded-2xl p-5">
            <ul className="divide-y divide-cyan-400/10">
              {STRATEGIES.slice(0, 6).map((s, i) => (
                <li key={s} className="flex items-center justify-between py-3 text-sm">
                  <span className="text-slate-200">{s}</span>
                  <span className={`text-lg ${i === activeIdx % 6 ? 'text-emerald-400' : 'text-slate-500'}`}>{i === activeIdx % 6 ? '✓' : '○'}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="glass-strong rounded-2xl p-8 flex flex-col items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 grid-overlay opacity-30" />
            <div className="absolute -inset-10 bg-cyan-500/10 blur-3xl -z-10" />
            <div className="font-display text-7xl sm:text-8xl font-bold text-cyan-gradient">33</div>
            <div className="mt-2 text-xs tracking-[0.3em] text-cyan-300/80">ENGINES</div>
            <div className="mt-6 h-1 w-40 progress-cyan rounded-full" />
          </div>

          <div className="glass rounded-2xl p-5">
            <ul className="divide-y divide-cyan-400/10">
              {STRATEGIES.slice(5).map((s, i) => (
                <li key={s} className="flex items-center justify-between py-3 text-sm">
                  <span className="text-slate-200">{s}</span>
                  <span className={`text-lg ${(i + 6) === activeIdx ? 'text-emerald-400' : 'text-slate-500'}`}>{(i + 6) === activeIdx ? '✓' : '○'}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function useCountUp(target, duration = 1400) {
  const [value, setValue] = useState(typeof target === 'string' ? target : 0);
  const ref = useRef(null);
  useEffect(() => {
    if (typeof target === 'string') return;
    let start = null;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      setValue(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return [value, ref];
}

function Stats() {
  return (
    <section className="relative py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionLabel eyebrow="Trusted Worldwide" title="The Numbers Behind The Engine" />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => <StatCard key={i} {...s} />)}
        </div>
      </div>
    </section>
  );
}

function StatCard({ value, suffix, label }) {
  const [v] = useCountUp(value);
  const display = typeof value === 'string' ? value : v.toLocaleString();
  return (
    <div className="glass rounded-2xl p-6 text-center feature-card">
      <div className="font-display text-4xl sm:text-5xl font-bold text-cyan-gradient">{display}{suffix}</div>
      <div className="mt-2 text-xs tracking-[0.3em] text-slate-400">{label}</div>
    </div>
  );
}

function Testimonials() {
  return (
    <section className="relative py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionLabel eyebrow="What Traders Say" title="Loved By The Community" />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="glass rounded-2xl p-6 feature-card">
              <div className="flex gap-1 text-amber-300">
                {Array.from({ length: 5 }).map((_, k) => <Star key={k} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="mt-3 text-slate-200 leading-relaxed">“{t.text}”</p>
              <div className="mt-4 text-sm text-cyan-300/80">— {t.name}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass-strong rounded-3xl p-10 text-center relative overflow-hidden">
          <div className="absolute -inset-16 bg-cyan-500/10 blur-3xl -z-10" />
          <div className="absolute inset-0 grid-overlay opacity-30" />
          <div className="relative">
            <div className="text-xs tracking-[0.3em] text-cyan-300/80">THE TERMINAL AWAITS</div>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl font-bold leading-tight">
              Ready To Unlock Your <span className="text-cyan-gradient">AI Dashboard?</span>
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a href={CONFIG.referralLink} target="_blank" rel="noreferrer" className="btn-cyan rounded-xl px-5 py-3 text-sm tracking-widest uppercase">
                Create {CONFIG.brokerName} Account
              </a>
              <Link to="/verify" className="btn-ghost rounded-xl px-5 py-3 text-sm tracking-widest uppercase">Verify Trader ID</Link>
              <Link to="/login" className="btn-ghost rounded-xl px-5 py-3 text-sm tracking-widest uppercase">Member Login</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TelegramBlock() {
  return (
    <section className="relative py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionLabel eyebrow="Join Our Growing Community" title="Join Our Official Telegram Community" />
        <div className="mt-8 grid gap-5 md:grid-cols-4">
          {[
            { title: 'Official Announcements', desc: 'Platform news & alerts' },
            { title: 'Market Updates', desc: 'Live market insights' },
            { title: 'Trading Education', desc: 'Strategies & guides' },
            { title: 'Community Support', desc: 'Trader-to-trader help' },
          ].map((it, i) => (
            <div key={i} className="glass rounded-2xl p-5 feature-card">
              <div className="h-10 w-10 rounded-lg glass flex items-center justify-center glow-cyan">
                <Send className="h-4 w-4 text-cyan-300" />
              </div>
              <div className="mt-3 font-display text-white font-semibold">{it.title}</div>
              <p className="mt-1 text-xs text-slate-400">{it.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <a href={CONFIG.telegramLink} target="_blank" rel="noreferrer" className="btn-cyan inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm tracking-widest uppercase">
            <Send className="h-4 w-4" /> Join Official Telegram Channel
          </a>
          <div className="mt-3 text-xs text-slate-500">Free · Official Channel · Secure Invite Link</div>
        </div>
      </div>

      {/* Floating telegram FAB */}
      <a href={CONFIG.telegramLink} target="_blank" rel="noreferrer" className="fixed bottom-6 right-6 z-40 btn-cyan rounded-full pl-4 pr-5 py-3 inline-flex items-center gap-2 shadow-lg">
        <Send className="h-4 w-4" /> <span className="text-xs tracking-[0.2em] uppercase">Telegram</span>
      </a>
    </section>
  );
}

function SectionLabel({ eyebrow, title }) {
  return (
    <div className="text-center">
      <div className="text-[11px] tracking-[0.35em] text-cyan-300/80">{eyebrow.toUpperCase()}</div>
      <h2 className="font-display mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold text-white">{title}</h2>
    </div>
  );
}
